import { cssThemedValue } from '@creditornot/cb-ingredients';
import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ReviewsInsight } from 'apis/merchant-gateway/insights/reviews/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';

import CompensationOffersSummary from './CompensationOffers/CompensationOffersSummary';
import { useIsOffersEnabled } from './CompensationOffers/useCompensationOffers';
import { InsightResponsePaginated } from '../../types';
import ErrorView from '../ErrorView';
import LoadingView from '../LoadingView';
import SectionHeader from '../SectionHeader';

type CompensationOffersCardProps = {
  reviewsInsights: InsightResponsePaginated<ReviewsInsight>;
  venue: Venue;
  onFilterBadReviews: () => void;
};

const Root = styled.div`
  position: relative;
  border: 1px solid ${cssThemedValue('borderPrimary')};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`;
const Divider = styled.div`
  border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
`;
const HeaderWrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ErrorContainer = styled.div`
  padding: 16px;
`;

const CompensationOffersCard = ({ reviewsInsights, venue, onFilterBadReviews }: CompensationOffersCardProps) => {
  const { formatMessage } = useIntl();

  const { isOffersEnabled } = useIsOffersEnabled(venue);

  // Hide the whole card if compensation offers are not enabled for the venue
  if (!isOffersEnabled) {
    return null;
  }

  const { data, isFetching, isError, refetch } = reviewsInsights;

  return (
    <Root>
      <HeaderWrapper>
        <SectionHeader
          title={formatMessage({ id: 'insights.ratings-and-reviews.offers-summary.title' })}
          tooltip={formatMessage({ id: 'insights.ratings-and-reviews.offers-summary.tooltip' })}
        />
      </HeaderWrapper>
      <Divider />
      {isFetching ? (
        <LoadingView />
      ) : isError ? (
        <ErrorContainer>
          <ErrorView onRetry={refetch} />
        </ErrorContainer>
      ) : (
        <CompensationOffersSummary
          venueId={venue.id}
          timezone={venue.timezone}
          reviewsData={data}
          onFilterBadReviews={onFilterBadReviews}
        />
      )}
    </Root>
  );
};

export default CompensationOffersCard;
